import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';

import { HistoryItems } from './history-items';
import { messages } from './messages';
import style from './style.module.less';
import ModalPortal from '../../../../../../../components/modal-portal';
import { useUtcFormattedDate } from '../../../../../../../hooks/use-datetime-formats';
import { ProductDetailsContext } from '../../../../../context';
import { locationNameFromParts } from '../../../../../display-helpers';

/**
 * @param {object} props
 * @param {() => void} props.onCloseModal
 * @param {string} [props.inventoryLocationId]
 * @param {string} [props.lot]
 * @param {string} [props.lotUuid]
 * @param {string} [props.expirationDate]
 * @returns {JSX.Element}
 */

export const InventoryHistoryModal = ({ onCloseModal, inventoryLocationId = undefined, lot = '', lotUuid = '', expirationDate = '' }) => {
  const { productName, productSku, inventoryWarehouses, inventoryWarehouseLocations } = useContext(ProductDetailsContext);
  const inventoryLocation = inventoryWarehouseLocations.find((loc) => loc.inventoryLocationId === inventoryLocationId);
  const warehouse = inventoryWarehouses.find((wrh) => wrh.inventoryWarehouseId === inventoryLocation?.inventoryWarehouseId);
  const formattedExpirationDate = useUtcFormattedDate(expirationDate);

  return (
    <ModalPortal onClose={onCloseModal}>
      <Modal size='custom' style={{ width: '80%' }}>
        <ModalHeader closeModal={onCloseModal}>
          <FormattedMessage {...messages.stockHistoryModalTitle} />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          <FlexRow className={style['history-row']}>
            <FlexColumn>
              <Text textType={TextType.Label} value={messages.inventoryHistoryModalName} />
              <Text textType={TextType.Paragraph} value={productName} />
            </FlexColumn>
            <FlexColumn>
              <Text textType={TextType.Label} value={messages.inventoryHistoryModalSku} />
              <Text textType={TextType.Paragraph} value={productSku} />
            </FlexColumn>
          </FlexRow>
          {inventoryLocationId ? (
            <>
              <FlexRow className={style['history-row']}>
                <FlexColumn>
                  <Text textType={TextType.Label} value={messages.inventoryHistoryModalWarehouse} />
                  <Text textType={TextType.Paragraph} value={warehouse.name} />
                </FlexColumn>
                <FlexColumn>
                  <Text textType={TextType.Label} value={messages.inventoryHistoryModalLocation} />
                  <Text textType={TextType.Paragraph} value={locationNameFromParts(inventoryLocation)} />
                </FlexColumn>
              </FlexRow>
              <FlexRow className={style['history-row']}>
                <FlexColumn>
                  <Text textType={TextType.Label} value={messages.inventoryHistoryModalLotID} />
                  <Text className={style.text} textType={TextType.Paragraph} value={lot ?? ''} />
                </FlexColumn>
                <FlexColumn>
                  <Text textType={TextType.Label} value={messages.inventoryHistoryExpirationDate} />
                  {expirationDate ? <Text className={style.text} textType={TextType.Paragraph} value={formattedExpirationDate} /> : null}
                </FlexColumn>
              </FlexRow>
            </>
          ) : null}
          <HistoryItems inventoryLocationId={inventoryLocationId} lotUuid={lotUuid} />
        </ModalBody>
        <ModalFooter className={style.footer}>
          <Button onClick={onCloseModal} theme='tertiary'>
            <FormattedMessage {...messages.inventoryHistoryModalClose} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
